import React, { useState, useEffect } from 'react';
import {
  getRegexRules,
  saveRegexRules,
  matchUrlRules,
  applyRulesToBookmarks,
  DEFAULT_REGEX_RULES,
} from '../lib/regexRouter';
import { playClick, playSuccess, playError } from '../lib/soundEffects';

export default function RegexRulesModal({
  isOpen,
  onClose,
  bookmarks = [],
  folders = [],
  onUpdateBookmarks,
}) {
  const [rules, setRules] = useState([]);
  const [testUrl, setTestUrl] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [newName, setNewName] = useState('');
  const [newPattern, setNewPattern] = useState('');
  const [newFolder, setNewFolder] = useState('');
  const [newTags, setNewTags] = useState('');
  const [batchResult, setBatchResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setRules(getRegexRules());
      setTestResult(null);
      setBatchResult(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        playClick();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleToggleRule = (id) => {
    playClick();
    const updated = rules.map((r) =>
      r.id === id ? { ...r, enabled: !r.enabled } : r
    );
    setRules(updated);
    saveRegexRules(updated);
  };

  const handleDeleteRule = (id) => {
    playClick();
    const updated = rules.filter((r) => r.id !== id);
    setRules(updated);
    saveRegexRules(updated);
  };

  const handleAddRule = (e) => {
    e.preventDefault();
    if (!newName.trim() || !newPattern.trim()) {
      playError();
      return;
    }

    try {
      new RegExp(newPattern.trim());
    } catch (err) {
      alert('Invalid Regular Expression pattern: ' + err.message);
      playError();
      return;
    }

    playSuccess();
    const tagList = newTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.startsWith('#') ? t : `#${t}`));

    const newRule = {
      id: 'rule_' + Date.now(),
      name: newName.trim(),
      pattern: newPattern.trim(),
      folderTarget: newFolder.trim() || null,
      tags: tagList,
      enabled: true,
    };

    const updated = [...rules, newRule];
    setRules(updated);
    saveRegexRules(updated);

    // Reset inputs
    setNewName('');
    setNewPattern('');
    setNewFolder('');
    setNewTags('');
  };

  const handleTestUrl = () => {
    playClick();
    if (!testUrl.trim()) {
      setTestResult(null);
      return;
    }
    const res = matchUrlRules(testUrl.trim(), rules, folders);
    setTestResult(res);
  };

  const handleExecuteBatch = () => {
    playClick();
    const res = applyRulesToBookmarks(bookmarks, rules, folders);
    setBatchResult(res);
    if (res.modifiedCount > 0) {
      playSuccess();
      if (onUpdateBookmarks) {
        onUpdateBookmarks(res.updatedBookmarks);
      }
    }
  };

  const handleResetDefaults = () => {
    playClick();
    if (confirm('Reset all regex routing rules to default presets?')) {
      setRules(DEFAULT_REGEX_RULES);
      saveRegexRules(DEFAULT_REGEX_RULES);
      playSuccess();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '740px',
          maxHeight: '90vh',
          backgroundColor: '#091017',
          border: '1px solid rgba(16, 185, 129, 0.35)',
          borderRadius: '16px',
          padding: '1.75rem',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9), 0 0 35px rgba(16, 185, 129, 0.15)',
          color: '#e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.75rem' }}>⚡</span>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#10b981', letterSpacing: '0.05em' }}>
                REGEX BOOKMARK ROUTING ENGINE
              </h2>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Auto-assign folders and tags via client-side regex pattern matching
              </div>
            </div>
          </div>
          <button
            onClick={() => { playClick(); onClose(); }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0.4rem',
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Container */}
        <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
          {/* Live Tester Bar */}
          <div
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1.25rem',
            }}
          >
            <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem', fontWeight: 600 }}>
              🧪 LIVE REGEX SIMULATOR
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Paste URL to test (e.g. https://github.com/torvalds/linux)..."
                value={testUrl}
                onChange={(e) => setTestUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTestUrl()}
                style={{
                  flex: 1,
                  padding: '0.6rem 0.8rem',
                  borderRadius: '6px',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }}
              />
              <button
                onClick={handleTestUrl}
                style={{
                  padding: '0.6rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#10b981',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                Test URL
              </button>
            </div>

            {testResult && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', padding: '0.6rem', borderRadius: '6px', backgroundColor: testResult.matched ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', border: '1px solid', borderColor: testResult.matched ? '#10b981' : '#ef4444' }}>
                {testResult.matched ? (
                  <div>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>✓ MATCHED: </span>
                    {testResult.matchedRules.map((r) => r.name).join(', ')}
                    <div style={{ marginTop: '0.3rem', color: '#94a3b8' }}>
                      Added Tags: {testResult.addedTags.map((t) => (
                        <span key={t} style={{ color: '#06b6d4', marginRight: '0.4rem' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#ef4444' }}>✕ No active regex rules matched this URL pattern.</div>
                )}
              </div>
            )}
          </div>

          {/* Active Rules List */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#e2e8f0' }}>
                ACTIVE ROUTING RULES ({rules.filter((r) => r.enabled).length}/{rules.length})
              </div>
              <button
                onClick={handleResetDefaults}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Reset to Defaults
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  style={{
                    backgroundColor: rule.enabled ? 'rgba(16, 185, 129, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid',
                    borderColor: rule.enabled ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ flex: 1, marginRight: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: rule.enabled ? '#10b981' : '#64748b', fontSize: '0.85rem' }}>
                        {rule.name}
                      </span>
                      {rule.folderTarget && (
                        <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '4px', backgroundColor: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4' }}>
                          📁 {rule.folderTarget}
                        </span>
                      )}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#94a3b8', margin: '0.2rem 0' }}>
                      {rule.pattern}
                    </div>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {(rule.tags || []).map((t) => (
                        <span key={t} style={{ fontSize: '0.7rem', color: '#38bdf8' }}>{t}</span>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleToggleRule(rule.id)}
                      style={{
                        padding: '0.3rem 0.6rem',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: rule.enabled ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
                        backgroundColor: rule.enabled ? 'rgba(16, 185, 129, 0.2)' : 'transparent',
                        color: rule.enabled ? '#10b981' : '#64748b',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      {rule.enabled ? 'Active' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#64748b',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                      }}
                      title="Delete rule"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add New Rule Form */}
          <form
            onSubmit={handleAddRule}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1rem',
            }}
          >
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#06b6d4', marginBottom: '0.6rem' }}>
              + ADD NEW REGEX ROUTING RULE
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                placeholder="Rule Name (e.g. Substack Newsletters)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{
                  padding: '0.5rem 0.7rem',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: '#fff',
                  fontSize: '0.8rem',
                }}
              />
              <input
                type="text"
                placeholder="Regex Pattern (e.g. \.substack\.com)"
                value={newPattern}
                onChange={(e) => setNewPattern(e.target.value)}
                style={{
                  padding: '0.5rem 0.7rem',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: '#fff',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Target Folder Name (Optional)"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                style={{
                  padding: '0.5rem 0.7rem',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: '#fff',
                  fontSize: '0.8rem',
                }}
              />
              <input
                type="text"
                placeholder="Tags, comma-separated (e.g. #news, #read)"
                value={newTags}
                onChange={(e) => setNewTags(e.target.value)}
                style={{
                  padding: '0.5rem 0.7rem',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: '#fff',
                  fontSize: '0.8rem',
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#06b6d4',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                Save Rule
              </button>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
            Library: {bookmarks.length} Bookmarks • {folders.length} Folders
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleExecuteBatch}
              style={{
                padding: '0.6rem 1.25rem',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#000',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                boxShadow: '0 0 15px rgba(16, 185, 129, 0.3)',
              }}
            >
              ⚡ Run All Rules Across Library
            </button>
          </div>
        </div>

        {batchResult && (
          <div style={{ marginTop: '0.75rem', padding: '0.6rem', borderRadius: '6px', backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981', color: '#10b981', fontSize: '0.8rem' }}>
            ✓ Batch routing complete! Updated {batchResult.modifiedCount} bookmarks matching active patterns.
          </div>
        )}
      </div>
    </div>
  );
}
